import React, { useState, useEffect, useMemo } from 'react';

import { useTrackingData, useBrandingData } from '../../dataProviders';

import AnimatedRouteSegment from './AnimatedRouteSegment';


const useMarkers = () => {
  const { events = [] } = useTrackingData();
  const orderedEvents = [...events].reverse();

  // Sample coords for going across the whole map
  // const events = [
  //   { latitude: '-35', longitude: '-90' },
  //   { latitude: '35', longitude: '90' }
  // ];
  const sameCityAndState = (e1,e2) => (
    [e1.city_locality,e1.state_province].join() === [e2.city_locality,e2.state_province].join()
  );

  return useMemo( () => {
    const uniqueLocations = orderedEvents.reduce( (locationEvents,event) => {
      if( event.longitude && event.latitude && (event.city_locality || event.state_province) &&
          !locationEvents.find( e => (e.longitude === event.longitude && e.latitude === event.latitude) ) &&
          !locationEvents.find( e => sameCityAndState(e, event) )
        ) {
        locationEvents.push(event);
      }
      return locationEvents;
    }, [] );

    return uniqueLocations.map( event => ({
      uniqueIdentifier: `${event.latitude},${event.longitude}`,
      name: [event.city_locality, event.state_province].join(','),
      longitude: event.longitude,
      latitude: event.latitude
    }));
  }, [orderedEvents] );
};


export default function AnimatedRoute({ zoom }) {
  const { colors: { tertiary: markerColor = '#000000' }={} } = useBrandingData();
  const markers = useMarkers();
  const markersFingerprint = markers.reduce( (signature,event) => signature + event.uniqueIdentifier, '' );
  const defaultState = useMemo( () => ({ currentMarker: null, lastLocationCoords: [null], markersFingerprint }), [markersFingerprint] );
  const [state,setState] = useState(defaultState);

  useEffect( () => {
    if( state.currentMarker === null ) {
      setTimeout( () => setState( prevState => ({ ...prevState, currentMarker: 0 }) ), 1000 );
    }
    // rerender whole route if any markers change
    if( markersFingerprint !== state.markersFingerprint ) {
      setState(defaultState);
    }
  }, [state.currentMarker, markersFingerprint, state.markersFingerprint, defaultState]);

  const onNextMarker = (lastLocationCoords) => {
    requestAnimationFrame( () => {
      setState({
        currentMarker: state.currentMarker + 1,
        lastLocationCoords: [...state.lastLocationCoords, lastLocationCoords],
        markersFingerprint
      });
    });
  };

  return markers.map( (marker,index) => (
    ( (state.currentMarker !== null) && index <= state.currentMarker) && (
      <AnimatedRouteSegment
        location={marker}
        lastLocationCoords={state.lastLocationCoords[index]}
        onFinished={onNextMarker}
        zoom={zoom}
        color={markerColor}
        key={marker.uniqueIdentifier}
      />
    )
  ));
}
